import type {
  SystemWorkspaceDefinition,
} from './SystemWorkspaceContracts';

import {
  buildTreeMutationAuthoritySummary,
} from '../tree-system/TreeMutationWriterRegistry';

const authority =
  buildTreeMutationAuthoritySummary();

export const treeSystemWorkspace:
  SystemWorkspaceDefinition = {
    id: 'tree',
    name: 'Family Tree',
    description:
      'Governs family-member identity, relationship creation, parent and child links, spouses, co-parents, claims, duplicates, merges, generation placement, branch ownership, repair, reconciliation, and rendering integrity.',

    owningGovernor:
      'Tree Governor',

    authoritySummary:
      'The Tree writer boundary is certified. Browser family_graph_members DML is contained to graphMutationGateway; FamilyGraphContext orchestrates twelve gateway call sites; server mutation is classified across active RPC authorities, dormant governed functions, and the reciprocal-spouse trigger.',

    health: 'degraded',

    statusSignals: {
      criticalFailure: false,
      simulationRunning: false,
      repairRunning: false,
      currentEvidenceAvailable: true,
      nonCriticalIssue: false,
      reason:
        `Tree writer authority is ${authority.status}: ${authority.confirmedDirectWriterFiles} browser direct-writer file owns ${authority.confirmedDirectWriteSites} DML sites, ${authority.gatewayRuntimeCallSites} gateway calls are active, and ${authority.effectiveServerWriterFunctions} server writer functions are classified.`,
    },

    authoritySources: [
      {
        id: 'tree-governor',
        kind: 'runtime_engine',
        label:
          'Tree relationship and connector policy',
        reference:
          'src/governor/tree',
        authoritative: true,
      },
      {
        id: 'tree-mutation-writer-registry',
        kind: 'policy',
        label:
          'Tree mutation writer classification',
        reference:
          'src/governor/tree-system/TreeMutationWriterRegistry.ts',
        authoritative: true,
      },
      {
        id: 'graph-mutation-gateway',
        kind: 'runtime_engine',
        label:
          'Designated graph mutation gateway',
        reference:
          'src/lib/graph/graphMutationGateway.ts',
        authoritative: true,
      },
      {
        id: 'family-graph-context',
        kind: 'frontend',
        label:
          'Production Tree mutation orchestrator',
        reference:
          'src/contexts/FamilyGraphContext.tsx',
        authoritative: false,
      },
      {
        id: 'family-graph-members',
        kind: 'database_table',
        label:
          'Persisted family member and relationship state',
        reference:
          'public.family_graph_members',
        authoritative: true,
      },
      {
        id: 'tree-runtime-simulator',
        kind: 'runtime_engine',
        label:
          'Tree runtime scenario simulator',
        reference:
          'src/governor/tree-runtime-simulator/TreeRuntimeSimulator.ts',
        authoritative: true,
      },
      {
        id: 'tree-runtime-bridge',
        kind: 'runtime_engine',
        label:
          'Tree runtime observation bridge',
        reference:
          'src/governor/tree-runtime/TreeRuntimeGovernorBridge.ts',
        authoritative: true,
      },
    ],

    dependencies: [
      {
        systemId: 'authentication',
        relationship: 'required',
        reason:
          'Claimed member identity and mutation permissions require authenticated users.',
      },
      {
        systemId: 'database',
        relationship: 'required',
        reason:
          'Family members, relationships, claims, confirmations, merges, media references, and repair evidence persist in Supabase.',
      },
      {
        systemId: 'membership',
        relationship: 'shared',
        reason:
          'Claimed, sponsored, free, and subscription identities affect profile and feature authority.',
      },
      {
        systemId: 'media',
        relationship: 'downstream',
        reason:
          'Photos, memories, and comments depend on stable canonical family-member IDs.',
      },
      {
        systemId: 'messaging',
        relationship: 'downstream',
        reason:
          'Family communication identity resolves through claimed family members.',
      },
      {
        systemId: 'calls',
        relationship: 'downstream',
        reason:
          'Call identity and allowed family recipients depend on claimed tree membership.',
      },
    ],

    certification: {
      source: 'pass',
      build: 'pass',
      simulatedRuntime: 'pending',
      deployedRuntime: 'pending',
      liveTransaction: 'fail',
      recovery: 'pending',
    },

    simulatorPolicy: {
      enabled: true,
      productionWritesAllowed: false,
      requiresExplicitLiveApproval: true,
      evidenceNamespace:
        'simulated:tree',
      supportedModes: [
        'SIMULATED',
        'REPLAYED',
        'HISTORICAL',
      ],
      resetRequired: true,
    },

    aiPolicy: {
      enabled: true,
      maximumAutonomousLevel: 3,
      allowedLevels: [0, 1, 2, 3],
      mayReadEvidence: true,
      mayGenerateScenarios: true,
      mayRecommendRepairs: true,
      mayPrepareChanges: true,
      mayExecuteProductionWrites: false,
      mustCiteEvidence: true,
      constitutionOverridesRecommendations: true,
    },

    milestone: {
      current:
        'CC-TREE-LIVE-003G3D1 — Post-merge security-mode and regression closure authority',
      completed: [
        'TG-001',
        'TG-002',
        'TG-003',
        'TG-004',
        'TG-005',
        'TG-006',
        'TG-007',
        'TG-008',
        'TG-009',
        'TG-010',
        'TG-011',
        'TG-012',
        'CP-016',
        'CP-056',
      ],
      pending: [
        'CC-TREE-000B — Enforce one Tree mutation authority',
        'CC-TREE-001A — Claimed profile mutation authority',
        'CC-TREE-001B — Parent, child, co-parent, and spouse authority',
        'CC-TREE-001C — Duplicate, merge, and canonical identity authority',
        'CC-TREE-001D — Generation, side, lane, and connector certification',
        'CC-TREE-001E — Governed repair and reconciliation authority',
        'CC-TREE-001F — Live family-tree runtime certification',
      ],
    },
  };
